import type {
  ResolveResponse,
  CreateMentionRequest,
  CreateMentionResponse,
  MentionApiErrorData,
  UserDetails,
} from './types';

export class MentionApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly retryAfter: number | null;
  readonly details?: Record<string, unknown> | undefined;

  constructor(
    code: string,
    message: string,
    status: number,
    retryAfter: number | null = null,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'MentionApiError';
    this.code = code;
    this.status = status;
    this.retryAfter = retryAfter;
    this.details = details;
  }
}

async function request<T>(url: string, init: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    let errorData: MentionApiErrorData = {
      code: 'UNKNOWN_ERROR',
      message: `Request failed: ${response.status}`,
    };
    try {
      const body = await response.json();
      if (body.error) errorData = body.error;
    } catch {
      /* use default */
    }
    const retryAfter =
      response.status === 429 ? Number(response.headers.get('Retry-After')) || 30 : null;
    throw new MentionApiError(
      errorData.code,
      errorData.message,
      response.status,
      retryAfter,
      errorData.details,
    );
  }
  return response.json();
}

export async function resolveUsers(
  baseUrl: string,
  token: string,
  params: { entityId: string; q: string; sourceRecordUrn?: string; limit?: number },
): Promise<ResolveResponse> {
  const url = new URL(`${baseUrl}/${params.entityId}/mentions/resolve`, window.location.origin);
  url.searchParams.set('q', params.q);
  if (params.sourceRecordUrn) url.searchParams.set('sourceRecordUrn', params.sourceRecordUrn);
  url.searchParams.set('limit', String(params.limit ?? 10));
  return request<ResolveResponse>(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function createMentions(
  baseUrl: string,
  token: string,
  entityId: string,
  body: CreateMentionRequest,
): Promise<CreateMentionResponse> {
  const url = new URL(`${baseUrl}/${entityId}/mentions`, window.location.origin);
  return request<CreateMentionResponse>(url.toString(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
}

export async function getUser(
  baseUrl: string,
  token: string,
  entityId: string,
  userId: string,
): Promise<UserDetails> {
  const url = new URL(
    `${baseUrl}/${entityId}/mentions/users/${encodeURIComponent(userId)}`,
    window.location.origin,
  );
  return request<UserDetails>(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  });
}
